/**
 * Public permalink: /player/:userId – public player profile. Viewable without login.
 * Shows stats when the viewer is logged in (premium/Ad-free); otherwise basic profile only if readable.
 */

import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePlan } from '../../contexts/PlanContext';
import { FEATURES } from '../../constants/plans';
import { useGame } from '../../contexts/GameContext';
import { GAMES, getGameById } from '../../constants/games';
import { getPlayerProfileData } from '../../api/readApi';
import DataFetchOverlay from '../../components/DataFetchOverlay';
import CachedAvatar from '../../components/CachedAvatar';
import PublicLayout from '../../components/PublicLayout';
import '../PublicPages.css';

export default function PublicPlayerPage() {
  const { userId } = useParams();
  const { currentUser } = useAuth();
  const { hasFeature } = usePlan();
  const { games: enabledGames } = useGame();
  const [profile, setProfile] = useState(null);
  const [statsDoc, setStatsDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  const showStats = currentUser && hasFeature(FEATURES.LEADERBOARD);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    if (!currentUser) {
      setProfile(null);
      setStatsDoc(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    getPlayerProfileData(userId)
      .then((data) => {
        if (cancelled) return;
        setProfile(data?.profile ?? null);
        setStatsDoc(data?.statsDoc ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setProfile(null);
          setStatsDoc(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId, currentUser]);

  const displayName = profile?.displayName || profile?.email || 'Player';
  const avatarUrl = profile?.photoURL || profile?.avatarURL || '';

  const getStatsForGame = (gameId) => {
    if (!statsDoc) return null;
    const gameStats = statsDoc.gameStats || {};
    const gs = gameStats[gameId];
    if (gs) {
      const matchesWon = gs.matchesWon ?? 0;
      const matchesLost = gs.matchesLost ?? 0;
      const total = matchesWon + matchesLost;
      return {
        matchesWon,
        matchesLost,
        totalPoints: gs.totalPoints ?? 0,
        totalMatches: total,
        winRate: total > 0 ? ((matchesWon / total) * 100).toFixed(1) : '0',
      };
    }
    return null;
  };

  const gamesToShow = Array.isArray(enabledGames) ? enabledGames : GAMES.map((g) => g.id);

  if (!userId) {
    return (
      <PublicLayout>
        <p>Player not specified.</p>
      </PublicLayout>
    );
  }

  if (loading) {
    return <DataFetchOverlay message="Loading player…" />;
  }

  return (
    <PublicLayout title="Player">
      <div className="public-card card-ui">
        <>
            <div className="public-player-header" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              <CachedAvatar userId={userId} photoURL={avatarUrl} displayName={displayName} size={64} />
              <div>
                <h2 style={{ margin: 0 }}>{displayName}</h2>
                {currentUser && (
                  <Link to={`/profile/${userId}`} className="public-link" style={{ fontSize: '0.9rem' }}>
                    Open full profile in app
                  </Link>
                )}
              </div>
            </div>
            {showStats && statsDoc && (
              <div className="public-player-stats" style={{ marginTop: 16 }}>
                <h3 style={{ margin: '0 0 12px', fontSize: '1rem' }}>Stats (by game)</h3>
                <ul className="public-list" style={{ listStyle: 'none', padding: 0 }}>
                  {gamesToShow.map((gid) => {
                    const game = getGameById(gid);
                    const stats = getStatsForGame(gid);
                    if (!stats || !game) return null;
                    return (
                      <li key={gid} style={{ padding: '8px 0', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
                        <Link to={`/sport/${gid}`} className="public-link" style={{ fontWeight: 600 }}>
                          {game.icon} {game.name}
                        </Link>
                        <span style={{ color: 'var(--muted-foreground)', marginLeft: 8 }}>
                          {stats.totalMatches} matches · {stats.matchesWon}W / {stats.matchesLost}L · Win rate {stats.winRate}%
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {!currentUser && (
              <p className="public-logged-in">Log in to see full profile and stats.</p>
            )}
        </>
      </div>
    </PublicLayout>
  );
}
